export type TooltipSide = 'top' | 'right' | 'bottom' | 'left'
export type TooltipAlign = 'start' | 'center' | 'end'

export interface TooltipRect {
  top: number
  left: number
  right: number
  bottom: number
  width: number
  height: number
}

/** Gap kept between the trigger and the tooltip, mirroring `var(--primitives-space-8)`. */
export const TRIGGER_GAP = 8

const SIDE_PRIMARY: Record<TooltipSide, (trigger: TooltipRect, content: TooltipRect) => number> = {
  top: (trigger, content) => trigger.top - content.height - TRIGGER_GAP,
  bottom: (trigger, _content) => trigger.bottom + TRIGGER_GAP,
  left: (trigger, content) => trigger.left - content.width - TRIGGER_GAP,
  right: (trigger, _content) => trigger.right + TRIGGER_GAP,
}

const HORIZONTAL_ALIGN: Record<TooltipAlign, (trigger: TooltipRect, content: TooltipRect) => number> = {
  start: (trigger, _content) => trigger.left,
  center: (trigger, content) => trigger.left + (trigger.width / 2) - (content.width / 2),
  end: (trigger, content) => trigger.right - content.width,
}

const VERTICAL_ALIGN: Record<TooltipAlign, (trigger: TooltipRect, content: TooltipRect) => number> = {
  start: (trigger, _content) => trigger.top,
  center: (trigger, content) => trigger.top + (trigger.height / 2) - (content.height / 2),
  end: (trigger, content) => trigger.bottom - content.height,
}

export function computeTooltipCoords(
  side: TooltipSide,
  align: TooltipAlign,
  triggerRect: TooltipRect,
  contentRect: TooltipRect,
): { top: number, left: number } {
  const isVertical = side === 'top' || side === 'bottom'
  const primary = SIDE_PRIMARY[side](triggerRect, contentRect)

  return {
    top: isVertical ? primary : VERTICAL_ALIGN[align](triggerRect, contentRect),
    left: isVertical ? HORIZONTAL_ALIGN[align](triggerRect, contentRect) : primary,
  }
}
